import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  FileText, Search, Filter, CheckCircle, XCircle, Clock, 
  DollarSign, MapPin, Calendar, User, Eye, RefreshCw,
  ArrowUpRight, Building2
} from 'lucide-react';

const AdminDeals = () => {
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchDeals();
  }, []);

  useEffect(() => {
    filterDeals();
  }, [deals, searchTerm, statusFilter]);

  const fetchDeals = async () => {
    try {
      const response = await adminAPI.getAllDeals();
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const filterDeals = () => {
    let filtered = [...deals];
    
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.deal_nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.property_city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.property_state.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }
    
    setFilteredDeals(filtered);
  };

  const updateDealStatus = async (dealId, newStatus) => {
    setUpdating(true);
    try {
      await adminAPI.updateDealStatus(dealId, newStatus);
      toast.success(`Deal status updated to ${newStatus}`);
      fetchDeals();
      setSelectedDeal(null);
    } catch (error) {
      toast.error('Failed to update deal status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
      under_review: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Eye },
      approved: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      active: { color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: ArrowUpRight },
      reserved: { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Building2 },
      completed: { color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
    };
    
    const config = statusConfig[status] || statusConfig.submitted;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const statusOptions = [
    'submitted', 'under_review', 'approved', 'active', 'reserved', 
    'docs_in_progress', 'liquidity_verified', 'closing', 'completed', 'cancelled'
  ];

  if (loading) {
    return (
      <Layout title="Manage Deals">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Manage Deals">
      <div className="space-y-6 animate-slide-in-up" data-testid="admin-deals">
        {/* Filters */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search deals by name, city, or state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="deal-search"
                />
              </div>
              <div className="w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="reserved">Reserved</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchDeals} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Deals</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{deals.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Pending Review</p>
              <p className="text-2xl font-bold text-blue-600">{deals.filter(d => d.status === 'submitted').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Active</p>
              <p className="text-2xl font-bold text-green-600">{deals.filter(d => d.status === 'active').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Completed</p>
              <p className="text-2xl font-bold text-slate-600">{deals.filter(d => d.status === 'completed').length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Deals List */}
        <div className="space-y-4">
          {filteredDeals.length === 0 ? (
            <Card className="glass-effect border-slate-200 dark:border-slate-700">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 dark:text-slate-400">No deals found</p>
              </CardContent>
            </Card>
          ) : (
            filteredDeals.map((deal) => (
              <Card 
                key={deal.id} 
                className="glass-effect border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all"
                data-testid={`deal-card-${deal.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          {deal.deal_nickname}
                        </h3>
                        {getStatusBadge(deal.status)}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <MapPin className="w-4 h-4" />
                          {deal.property_city}, {deal.property_state}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <DollarSign className="w-4 h-4" />
                          ${deal.purchase_price?.toLocaleString()}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <Calendar className="w-4 h-4" />
                          {new Date(deal.est_closing_date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
                          <DollarSign className="w-4 h-4" />
                          Liquidity: ${deal.liquidity_amount_provided?.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedDeal(selectedDeal?.id === deal.id ? null : deal)}
                        data-testid={`view-deal-${deal.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {selectedDeal?.id === deal.id ? 'Hide' : 'Details'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {selectedDeal?.id === deal.id && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 animate-slide-in-up">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Deal Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Deal ID:</span> <span className="text-slate-900 dark:text-slate-100">{deal.id}</span></p>
                            <p><span className="text-slate-500">Full Address:</span> <span className="text-slate-900 dark:text-slate-100">{deal.property_full_address}</span></p>
                            <p><span className="text-slate-500">Purchase Price:</span> <span className="text-slate-900 dark:text-slate-100">${deal.purchase_price?.toLocaleString()}</span></p>
                            <p><span className="text-slate-500">Rehab Budget:</span> <span className="text-slate-900 dark:text-slate-100">${deal.rehab_budget?.toLocaleString()}</span></p>
                            <p><span className="text-slate-500">ARV:</span> <span className="text-slate-900 dark:text-slate-100">${deal.arv?.toLocaleString()}</span></p>
                            <p><span className="text-slate-500">Exit Strategy:</span> <span className="text-slate-900 dark:text-slate-100">{deal.exit_strategy}</span></p>
                            <p><span className="text-slate-500">Lender:</span> <span className="text-slate-900 dark:text-slate-100">{deal.lender_name}</span></p>
                            <p><span className="text-slate-500">Reserve Required:</span> <span className="text-green-600 font-medium">${deal.reserve_required_amount?.toLocaleString()}</span></p>
                            {deal.notes && <p><span className="text-slate-500">Notes:</span> <span className="text-slate-900 dark:text-slate-100">{deal.notes}</span></p>}
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Update Status</h4>
                          <div className="space-y-3">
                            <Select 
                              value={deal.status} 
                              onValueChange={(value) => updateDealStatus(deal.id, value)}
                              disabled={updating}
                            >
                              <SelectTrigger data-testid={`status-select-${deal.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map(status => (
                                  <SelectItem key={status} value={status}>
                                    {status.replace('_', ' ').toUpperCase()}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            <div className="flex gap-2">
                              {deal.status === 'submitted' && (
                                <>
                                  <Button
                                    size="sm"
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                    onClick={() => updateDealStatus(deal.id, 'approved')}
                                    disabled={updating}
                                    data-testid={`approve-deal-${deal.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="flex-1"
                                    onClick={() => updateDealStatus(deal.id, 'cancelled')}
                                    disabled={updating}
                                    data-testid={`reject-deal-${deal.id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </>
                              )}
                              {deal.status === 'approved' && (
                                <Button
                                  size="sm"
                                  className="flex-1 gradient-primary border-0"
                                  onClick={() => updateDealStatus(deal.id, 'active')}
                                  disabled={updating}
                                >
                                  <ArrowUpRight className="w-4 h-4 mr-1" />
                                  Make Active (List in Marketplace)
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminDeals;
